import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { LogOut, Menu, ShoppingCart } from "lucide-react";
import Logo from "@/components/Logo";

export default function DashboardLayout({ nav, title, children }) {
  const { user, logout } = useAuth();
  const cart = useCart();
  const nav1 = useNavigate();
  const [open, setOpen] = useState(false);
  const handleLogout = () => { logout(); nav1("/login"); };
  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className={`${open ? "block fixed inset-0 z-40" : "hidden"} lg:block lg:static w-64 shrink-0 bg-white border-r border-slate-200`}>
        <div className="h-20 px-5 flex items-center border-b border-slate-200">
          <Link to="/" aria-label="SchoolLane – Home" className="inline-block">
            <Logo height={44} />
          </Link>
        </div>
        <nav className="p-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end onClick={() => setOpen(false)}
              data-testid={`side-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-blue-50 text-brand-blue"
                    : n.highlight
                      ? "bg-brand-green text-white shadow-sm hover:bg-brand-greenDark"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }>
              {n.icon && <n.icon size={16} />}
              <span className="flex-1">{n.label}</span>
              {n.label === "Cart" && (cart?.totalUnits || 0) > 0 && (
                <span className="text-[10px] font-bold bg-brand-blue text-white rounded-full min-w-[20px] h-5 grid place-items-center px-1.5" data-testid="side-cart-badge">
                  {cart.totalUnits}
                </span>
              )}
              {n.highlight && !(n.label === "Cart") && (
                <span className="text-[10px] font-bold uppercase tracking-wide">Shop</span>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 h-16 backdrop-blur-xl bg-white/85 border-b border-slate-200 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" data-testid="dash-menu" onClick={() => setOpen(!open)}><Menu size={20} /></button>
            <h1 className="font-heading font-semibold text-lg text-slate-900">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === "parent" && (
              <Link to="/parent/cart" data-testid="header-cart"
                    className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-slate-700 hover:bg-slate-100 border border-slate-200 text-sm font-semibold">
                <ShoppingCart size={16} />
                <span className="hidden sm:inline">Cart</span>
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[11px] font-bold bg-brand-blue text-white rounded-full">
                  {cart?.totalUnits || 0}
                </span>
              </Link>
            )}
            <Link to="/" className="text-sm text-slate-500 hover:text-slate-800 hidden sm:inline">View site</Link>
            <div className="hidden sm:flex flex-col items-end text-right">
              <span className="text-sm font-semibold text-slate-800" data-testid="user-name">{user?.name}</span>
              <span className="text-xs text-slate-500 capitalize">{user?.role?.replace("_", " ")}</span>
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-blue to-brand-green text-white grid place-items-center text-sm font-semibold">
              {user?.name?.[0]}
            </div>
            <button onClick={handleLogout} data-testid="logout-btn" className="ml-2 p-2 rounded-md hover:bg-slate-100 text-slate-500">
              <LogOut size={18} />
            </button>
          </div>
        </header>
        <main className="p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
